import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { UploadCloud } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { projectsQuery } from '@/api/hooks/projects';
import { Button, Popover, ProgressBar } from '@/components';
import { formatBytes, formatDuration, formatPercent, formatRate } from '@/lib/format';
import { paths } from '@/routing';
import { isUploadActive, pauseUpload, useUploads, type UploadRunState } from '@/stores/uploads';
import styles from './TopBar.module.css';

const PHASE: Record<string, string> = {
  preparing: 'Preparing…',
  uploading: 'Uploading',
  completing: 'Finishing…',
  done: 'Uploaded',
  failed: 'Failed',
  aborted: 'Cancelled',
  paused: 'Paused',
};

/**
 * Upload progress in the top bar: uploads run in the app-level store and continue while
 * the user works on other pages. Hidden when no upload is running or waiting to resume.
 */
export function UploadIndicator() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const runs = useUploads((s) => s.runs);
  const shown = Object.values(runs).filter(
    (run) => run.progress && run.progress.phase !== 'done' && run.progress.phase !== 'aborted',
  );
  const projects = useQuery({ ...projectsQuery(), enabled: shown.length > 0 });
  if (shown.length === 0) return null;
  const active = shown.filter((run) => isUploadActive(run.progress));
  const uploaded = shown.reduce((n, run) => n + (run.progress?.uploaded ?? 0), 0);
  const total = shown.reduce((n, run) => n + (run.progress?.total ?? 0), 0);
  const fraction = total > 0 ? uploaded / total : 0;
  const name = (run: UploadRunState) =>
    projects.data?.find((p) => p.id === run.projectId)?.name ?? `Project ${run.projectId}`;
  const failed = shown.some((run) => run.progress?.phase === 'failed');

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={clsx(styles.iconTrigger, active.length > 0 && styles.busy, failed && styles.failed)}
        aria-label={
          active.length > 0
            ? `Uploading: ${formatPercent(fraction)}`
            : failed
              ? 'Upload failed'
              : 'Upload paused'
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="upload-indicator"
      >
        <UploadCloud aria-hidden />
        <span className={clsx(styles.uploadPct, 'num')}>{formatPercent(fraction)}</span>
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        placement="bottom-end"
        aria-label="Uploads"
        className={styles.jobsPanel}
        autoFocus={false}
      >
        <div className={styles.jobsHeader}>
          <h2>Uploads</h2>
        </div>
        <ul className={styles.jobsList}>
          {shown.map((run) => {
            const p = run.progress!;
            const f = p.total > 0 ? p.uploaded / p.total : 0;
            return (
              <li key={run.runId} className={styles.jobItem}>
                <div className={styles.jobLine}>
                  <span className="truncate">
                    {PHASE[p.phase] ?? p.phase} · {name(run)}
                  </span>
                  <span className="num subtle">{formatPercent(f)}</span>
                </div>
                <ProgressBar
                  value={p.phase === 'preparing' ? null : f}
                  label={`Upload to ${name(run)}`}
                  size="sm"
                  tone={p.phase === 'failed' ? 'danger' : p.phase === 'paused' ? 'warning' : 'accent'}
                />
                <span className="subtle small num">
                  {formatBytes(p.uploaded)} of {formatBytes(p.total)}
                  {p.phase === 'uploading' && p.rate > 0 && ` · ${formatRate(p.rate)}`}
                  {p.phase === 'uploading' && p.eta !== null && ` · ${formatDuration(p.eta)} left`}
                </span>
                <div className={styles.uploadActions}>
                  {p.phase === 'uploading' && (
                    <Button size="sm" variant="ghost" onClick={() => pauseUpload(run.projectId)}>
                      Pause
                    </Button>
                  )}
                  <Link className="small" to={paths.upload(run.projectId)} onClick={() => setOpen(false)}>
                    Open the upload page
                  </Link>
                </div>
                {p.error && <span className="danger-text small">{p.error}</span>}
              </li>
            );
          })}
        </ul>
      </Popover>
    </>
  );
}
